<template>
  <div class="terminal-container">
    <div v-if="showInstructions" class="instructions">
      <h1>{{ $t('instructions.title') }}</h1>
      <p v-html="$t('instructions.instructions')"></p>
      <p v-html="$t('instructions.subinstruction')"></p>

      <p v-html="$t('instructions.facts.fact_1')"></p>
      <p v-html="$t('instructions.facts.fact_2')"></p>
      <p v-html="$t('instructions.facts.fact_3')"></p>
      <p v-html="$t('instructions.facts.fact_4')"></p>

      <!--<p class="bold">{{ $t('instructions.memo_prompt') }}</p>-->
      <!-- <CountDown @onfinished="startRecording" :from="timer">{{ $t('instructions.countdown_message') }}</CountDown> -->
      <button @click="startRecording">Starta</button>
    </div>

    <div v-if="!showInstructions && !isRecording && !aiResult && !isAnalyzing" class="instructions">
      <h1>{{ $t('memory_test.title') }}</h1>

      <div class="tent-images">
        <div class="tent">
          <img src="images/tent_a.png" alt="">
          <span class="letter">A</span>
        </div>
        <div class="tent">
          <img src="images/tent_b.png" alt="">
          <span class="letter">B</span>
        </div>
      </div>
      <p v-html="$t('intro.text')" class="center-text"></p>
      <!--<p v-html="$t('memory_test.descripti">) }}</p>-->

      <button @click="showInstructions = true" class="start-button">{{ $t('memory_test.start_button') }}</button>
    </div>

    <div v-if="isRecording">
      <div class="instructions">
        <h1>{{ $t('memory_test.title') }}</h1>
        <p :class="{ active: activeInstructionIndex === 1 }" v-html="$t('instructions.facts.fact_1')"></p>
        <p :class="{ active: activeInstructionIndex === 2 }" v-html="$t('instructions.facts.fact_2')"></p>
        <p :class="{ active: activeInstructionIndex === 3 }" v-html="$t('instructions.facts.fact_3')"></p>
        <p :class="{ active: activeInstructionIndex === 4 }" v-html="$t('instructions.facts.fact_4')"></p>
      </div>

      <button v-if="isRecording" @click="stopRecordingManually">
        Klar! Analysera nu
      </button>

      <CountDown :from="answerTimer" @onchange="currentTime = answerTimer - $event"></CountDown>
      <div id="status">
        <div class="prompt">{{ $t('recording.prompt') }}</div>
      </div>
      <span v-html="transcript"></span>
    </div>

    <div v-else>
      <div v-if="isAnalyzing">
        <h1>{{ $t('analysis.analyzing') }}</h1>
      </div>
      <div v-if="!aiResult && status">
        <div id="status">
          <div class="prompt">{{ $t('analysis.status_prefix') }}{{ status }}</div>
        </div>
      </div>
    </div>

    <p id="transcript" v-if="aiResult">
      <span class="big">{{ score }}{{ $t('analysis.score_prefix') }}</span>
      <span v-html="aiResult"></span>

      <button @click="reset">{{ $t('analysis.reset_button') }}</button>
    </p>

    <div class="scanlines"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import CountDown from './components/CountDown.vue';
import { useI18n } from 'vue-i18n';

const { locale, availableLocales, t } = useI18n();

const serverUrl = 'https://gpt.2047.degig.se';
//const serverPort = '80';
const showInstructions = ref(false);
const status = ref(false);
const transcript = ref(false);
const score = ref(0)
const timer = ref(30)
const answerTimer = ref(90)
const isRecording = ref(false)
let recognition;
let recordingTimeout;
let finalTranscript = ''
const aiResult = ref(false)
const isAnalyzing = ref(false)

let isManuallyStopped = false;

const currentTime = ref(0)

onMounted(() => {
  if (typeof window !== 'undefined') {
    const queryString = window.location.search;

    const urlParams = new URLSearchParams(queryString);

    const urlLocale = urlParams.get('locale');

    if (urlLocale) {
      if (availableLocales.includes(urlLocale)) {
        locale.value = urlLocale;
        status.value = t('status_messages.waiting_to_start')
        console.log(`Språk ändrat till: ${urlLocale}`);
      } else {
        console.warn(`Ogiltig locale i URL:en: ${urlLocale}. Använder standard.`);
      }
    }
  }
});

const activeInstructionIndex = computed(() => {
  const progress = currentTime.value / answerTimer.value

  if (progress <= 0.25) return 1
  if (progress <= 0.50) return 2
  if (progress <= 0.75) return 3
  return 4
})

const getSpeechRecognitionLangCode = (shortLocale) => {
  switch (shortLocale) {
    case 'sv':
      return 'sv-SE';
    case 'no':
    case 'nb':
      return 'nb-NO'; // Norsk Bokmål i Norge
    // Lägg till fler språk vid behov
    default:
      return 'sv-SE'; // Fallback
  }
};

function stopRecordingManually() {
  console.log("button was pressed")
  isManuallyStopped = true;
  recordingTimeout = Date.now(); // Gör att onend går till "else"-blocket (fetch)
  if (recognition) {
    recognition.stop();
  }
  
}

function reset() {

  showInstructions.value = false;
  status.value = t('status_messages.waiting_to_start');
  transcript.value = false
  score.value = 0

  isRecording.value = false

  finalTranscript = '';
  aiResult.value = false
}



function startRecognition() {
  recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
  recognition.lang = getSpeechRecognitionLangCode(locale.value);
  recognition.interimResults = true;

  recognition.onresult = (event) => {
    let interimTranscript = '';
    for (let i = event.resultIndex; i < event.results.length; ++i) {
      if (event.results[i].isFinal) {
        finalTranscript += event.results[i][0].transcript;
      } else {
        interimTranscript += event.results[i][0].transcript;
      }
    }
    transcript.value = finalTranscript + interimTranscript;
  };

  recognition.onerror = (event) => {
    console.error('Fel vid igenkänning:', event.error);
    status.value = t('status_messages.error_prefix') + event.error;
  };

  recognition.onend = () => {
    // Om vi har stoppat manuellt eller tiden gått ut
    if (Date.now() < recordingTimeout && !isManuallyStopped) {
      console.log("Time has not timed out. Keep listening.")
      transcript.value += ".<br>"
      setTimeout(startRecognition, 0);
    } else {
      console.log("Time is out. Lets stop and analyze")
      isRecording.value = false;

      // VIKTIGT: Om det finns interim-text kvar (det man precis sa), 
      // lägg till det i finalTranscript innan fetch.
      const finalResult = transcript.value.replace(/<br>/g, '');
      console.log(finalResult)
      if (finalResult) {
        isAnalyzing.value = true;
        const currentLang = locale.value;
        const urlWithLang = `${serverUrl}/remember/do?lang=${currentLang}`;
        fetch(urlWithLang, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ content: finalTranscript }),
        })
          .then(response => response.json())
          .then(data => {
            isAnalyzing.value = false

            aiResult.value = data.comment
            score.value = data.score
          })
          .catch((error) => {
            isAnalyzing.value = false
            console.error('Error:', error);
            transcript.value += '\nFel vid kommunikation med servern: ' + error;
          });
      }
      else {
        status.value ="You need to speak. "
      }
    }
  };

  recognition.start();
}

function startRecording() {
  isRecording.value = true
  isManuallyStopped = false
  showInstructions.value = false;
  status.value = t('status_messages.recording_in_progress')
  transcript.value = ''
  finalTranscript = ''

  const endTime = Date.now() + answerTimer.value * 1000
  recordingTimeout = endTime

  startRecognition()

  setTimeout(() => {
    recognition.stop()
    status.value = t('status_messages.stopped_after_30s')
  }, timer.value * 1000)
}
</script>


<style>
* {
  box-sizing: border-box;
}

:root {
  --color-primary: #ff9500;
  /* Ljus grön terminal-färg */
  --color-background: #001f0e;
  /* Mycket mörkgrön/nästan svart bakgrund */
  --font-terminal: 'VT323', monospace;
  /* Monospace font */
}

/* Nollställ standardmarginaler och använd terminal-font */
body {
  background-color: #000000;
  /* Svart bakgrund för att kontrastera med skärmen */
  color: var(--color-primary);
  font-family: var(--font-terminal);
  font-size: 1.8em;
  margin: 0;
  padding: 0px;
  height: 100vh;
  box-sizing: border-box;
  overflow: hidden;
  /* För att undvika scroll under CRT-effekten */
}

/* Styla själva "skärmen" */
.terminal-container {
  border: 5px solid var(--color-primary);
  box-shadow: 0 0 10px var(--color-primary), 0 0 20px var(--color-primary) inset;
  /* Glöd-effekt */
  padding: 20px;
  background-color: var(--color-background);
  width: 100vw;
  height: 100vh;
  margin: 0 auto;
  position: relative;
  z-index: 10;

  animation:
    screen-flicker 4s infinite,
    minor-glitch 12s steps(1) infinite;

}

.bordered {
  border: 2px solid var(--color-primary);
}

.scanlines {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 20;
  /* Över all annan design */
  pointer-events: none;
  /* Se till att det inte blockerar klick */

  /* Skapa horisontella, svagt transparenta linjer */
  background:
    repeating-linear-gradient(0deg,
      rgba(0, 0, 0, 0.15),
      rgba(0, 0, 0, 0.15) 1px,
      transparent 1px,
      transparent 2px);

  /* Lägg till en svag dimma/brus-effekt */
  opacity: 0.6;
}

h1,
h2,
h3,
h4 {
  font-family: var(--font-terminal);
}

.prompt {
  display: inline-block;

  /* Bevara mellanslag */
}

.prompt::after {
  content: '';
  display: inline-block;
  width: 0.5em;
  /* Markörens bredd */
  height: 1em;
  background-color: var(--color-primary);
  animation: blink-cursor 1s step-end infinite;
  vertical-align: middle;
  margin: 0px 5px;
}

@keyframes blink-cursor {

  from,
  to {
    opacity: 1;
  }

  50% {
    opacity: 0;
  }
}

strong {
  color: #fff;
}

#status {
  text-align: center;
}

button {
  display: block;
  margin: 0 auto;
  background-color: transparent;
  color: var(--color-primary);
  font-family: var(--font-terminal);
  font-size: 1.2em;
  text-transform: uppercase;

  border: 2px solid var(--color-primary);
  outline: none;
  text-shadow: 0 0 5px var(--color-primary);
  box-shadow: 0 0 5px var(--color-primary);

  padding: 10px 20px;
  cursor: pointer;
  transition:
    background-color 0.1s,
    color 0.1s,
    box-shadow 0.1s;
}

button:hover {
  background-color: rgba(0, 255, 65, 0.2);
  box-shadow: 0 0 10px var(--color-primary), 0 0 15px var(--color-primary) inset;
}

button:active {
  background-color: var(--color-primary);
  color: var(--color-background);
  text-shadow: none;
}


.center-text {
  text-align: center;
}

#transcript {
  font-size: 40px;
  font-weight: bold;
  padding: 20px;
  width: 80%;
  margin: 0 auto;
}

#transcript button {
  display: block;
  margin-top: 40px; 
}

.big {
  font-size: 60px;
  display: block;
  margin: 20px auto;
}

.instructions {
  text-align: left;
  width: 80%;
  margin: 20px auto;

}

.instructions p {
  white-space: pre-line;
}

.instructions .active {
  color: #fff;
}

.instructions button {
  display: block;
  margin: 40px auto;
}

.bold {
  font-size: 1.1em;
  font-weight: bold;
}

.tent-images {
  width: 100%;
  display: flex;
  justify-content: space-between;
}

.tent-images .tent {
  display: block;
  max-width: 48%;
  position: relative;
}

.tent-images .tent span.letter {
  position: absolute;
  top: 5%;
  left: 5%;
  font-size: 2em;
  color: #fff;
  display: block;
  padding: 5px 20px;
  text-align: center;
  background-color: #ff9500;
}

.tent-images .tent img {
  max-width: 100%;
}

@keyframes screen-flicker {
  0% {
    opacity: 1;
    /* Full opacitet */
    filter: brightness(1);
    /* Normal ljusstyrka */
    /* Mindre glöd-variation */
    box-shadow: 0 0 10px var(--color-primary), 0 0 20px var(--color-primary) inset;
  }

  /* Endast en liten, snabb fluktuation */
  5% {
    opacity: 0.99;
    /* Nästan full opacitet */
    filter: brightness(1.01) contrast(1.01);
    /* Minimal ökning i ljusstyrka */
    /* Mycket liten glöd-variation */
    box-shadow: 0 0 9px var(--color-primary), 0 0 19px var(--color-primary) inset;
  }

  10% {
    opacity: 1;
    filter: brightness(1);
  }

  /* Mindre "dimming" vid 50% */
  50% {
    opacity: 0.995;
    /* Nästan osynlig dimming */
    filter: brightness(0.99);
    /* Minimal dimning */
    box-shadow: 0 0 10px var(--color-primary), 0 0 20px var(--color-primary) inset;
  }

  55% {
    opacity: 1;
    filter: brightness(1);
  }

  100% {
    opacity: 1;
    filter: brightness(1);
  }
}

@keyframes minor-glitch {
  0% {
    transform: translateY(0);
  }

  1% {
    transform: translateY(-1px);
  }

  /* Liten uppåt-förskjutning */
  2% {
    transform: translateY(1px);
  }

  /* Liten nedåt-förskjutning */
  3% {
    transform: translateY(0);
  }
}
</style>
